// Storage for a student invite token handed off across the register/confirm-email/login
// round trip (localStorage, not sessionStorage, because the confirmation email link is
// commonly opened in a NEW tab — see AuthCallback.tsx). Kept as a thin wrapper around a pure
// freshness check so the routing-relevant decision (is this token still worth honoring?) is
// unit-testable without touching the DOM storage APIs.
//
// A token is cleared after every accept-invite ATTEMPT (success or failure), not only on
// success — see SelectRole.tsx. Without that, a token from an expired/wrong-email/already-used
// invitation would sit in localStorage forever and silently reroute every later login on that
// browser (even a different student's) through select-role to re-attempt a token already known
// to fail. The timestamp-based expiry below is the second, independent layer: it also catches
// a token that was saved but never submitted at all (e.g. the user closed the tab on the
// "check your email" step), so an abandoned token does not linger indefinitely either.

const TOKEN_KEY    = 'dt_pending_invite_token'
const SAVED_AT_KEY = 'dt_pending_invite_saved_at'

// Generous on purpose: the primary defense against a stale/known-bad token contaminating a
// later login is SelectRole.tsx clearing it after every accept-invite ATTEMPT (success or
// failure), regardless of this TTL. This expiry only catches a token that was saved but never
// submitted at all (e.g. the tab was closed on the "check your email" step). It must stay
// comfortably longer than a realistic register → confirm-email → log-in round trip, AND than
// the "existing account → reset forgotten password → log in" path (Register.tsx's
// existing-account panel links to /forgot-password without touching this token), both of
// which can legitimately take hours if the user doesn't check their inbox right away.
export const PENDING_INVITE_MAX_AGE_MS = 24 * 60 * 60 * 1000 // 24 hours

/** Pure: is a token saved at `savedAt` still fresh enough to act on, judged at `now`? */
export function isPendingInviteFresh(savedAt: number, now: number): boolean {
  if (!Number.isFinite(savedAt) || !Number.isFinite(now)) return false
  const age = now - savedAt
  return age >= 0 && age < PENDING_INVITE_MAX_AGE_MS
}

export function savePendingInviteToken(token: string): void {
  try {
    localStorage.setItem(TOKEN_KEY, token)
    localStorage.setItem(SAVED_AT_KEY, String(Date.now()))
  } catch { /* storage unavailable — token still usable for the rest of this page's lifetime */ }
}

/** Returns the token only if present and still fresh; clears it (and the timestamp) otherwise. */
export function readPendingInviteToken(now: number = Date.now()): string | null {
  try {
    const token = localStorage.getItem(TOKEN_KEY)
    if (!token) return null
    const savedAt = Number(localStorage.getItem(SAVED_AT_KEY))
    if (!isPendingInviteFresh(savedAt, now)) {
      clearPendingInviteToken()
      return null
    }
    return token
  } catch {
    return null
  }
}

export function clearPendingInviteToken(): void {
  try {
    localStorage.removeItem(TOKEN_KEY)
    localStorage.removeItem(SAVED_AT_KEY)
  } catch { /* ignore */ }
}

/**
 * Pure: should SelectRole.tsx clear the saved token after an accept-invite call?
 *
 * Only when the server actually rendered a verdict (`hadTransportError === false`) — whether
 * that verdict was acceptance or a structured rejection (expired/email-mismatch/etc). A
 * transport-level failure (network drop, timeout, 5xx) means the token's validity is still
 * genuinely unknown, so it must be preserved for a retry rather than discarded.
 */
export function shouldClearPendingInviteToken(hadTransportError: boolean): boolean {
  return !hadTransportError
}
